import { useHasAccess } from '@components/HasAccess'
import { subscriptionRequestStatuses } from '@models/membership/Subscription'
import { fetchSubscriptionStatusCounts } from '@services/membership/SubscriptionService'
import { useAppSelector } from '@store/hooks'
import { useFeature } from '@utils/featureFlags'
import { Permission } from '@utils/permissions'
import { Badge, Col, Layout, Row, Space, Tag, theme } from 'antd'
import { useEffect, useState } from 'react'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import { LayoutFooter } from '../components/LayoutFooter'
import MainHeader from '../components/MainHeader'

const { Content } = Layout

export const MembershipLayout: React.FC = () => {
  const { hasAccess } = useHasAccess()
  const { token } = theme.useToken()
  const navigate = useNavigate()
  const { enabled: revenueEnabled } = useFeature('membershipAnalyticsOverview')

  const [requestsCount, setRequestsCount] = useState(0)
  // Any change to the loaded subscriptions (accept, reject, ...) may move one
  // in or out of the request statuses, hence a recount.
  const subscriptions = useAppSelector((state) => state.subscription.items)

  let subItems = [
    {
      key: 'requests',
      label: (
        <Space size={5}>
          <Link to={'/membership/requests'}>Requests</Link>
          <Badge
            count={requestsCount}
            styles={{ indicator: { boxShadow: 'none' } }}
          />
        </Space>
      ),
      hasAccess: hasAccess(Permission.manageRequestsMembership),
    },
    {
      key: 'members',
      label: <Link to={'/membership/members'}>Members</Link>,
      hasAccess: hasAccess(Permission.manageMembersMembership),
    },
    {
      key: 'events',
      label: <Link to={'/membership/events'}>Events</Link>,
      hasAccess: hasAccess(Permission.manageEventsMembership),
    },
    {
      key: 'events-collections',
      label: <Link to={'/membership/events-collections'}>Collections</Link>,
      hasAccess: hasAccess(Permission.manageEventsMembership),
    },
    {
      key: 'analytics',
      label: <Link to={'/membership/analytics'}>Analytics</Link>,
      hasAccess: hasAccess(Permission.manageMembersMembership),
    },
    {
      key: 'revenue',
      label: (
        <Space size={8}>
          <Link to={'/membership/revenue'}>Revenue</Link>
          <Tag
            color={token.colorPrimary}
            style={{ marginInlineEnd: 0, cursor: 'pointer' }}
            onClick={() => navigate('/membership/revenue')}
          >
            New
          </Tag>
        </Space>
      ),
      // Both the feature flag and the dedicated permission.
      hasAccess: revenueEnabled && hasAccess(Permission.viewRevenueMembership),
    },
    {
      key: 'coupons',
      label: <Link to={'/membership/coupons'}>Coupons</Link>,
      hasAccess: hasAccess(Permission.manageRequestsMembership),
    },
    {
      key: 'benefits',
      label: <Link to={'/membership/benefits'}>Benefits</Link>,
      hasAccess: hasAccess(Permission.managePerksMembership),
    },
  ]

  useEffect(() => {
    fetchSubscriptionStatusCounts()
      .then(({ data }) =>
        setRequestsCount(
          subscriptionRequestStatuses.reduce(
            (total, status) => total + (data[status] ?? 0),
            0
          )
        )
      )
      .catch(() => {})
  }, [subscriptions])

  return (
    <Layout>
      <MainHeader subItems={subItems.filter((item) => item.hasAccess)} />
      <Layout style={{ minHeight: '100vh', marginTop: 128 }}>
        <Content>
          <Row className="container-row">
            <Col xs={24} className="px-4">
              <Outlet />
            </Col>
          </Row>
        </Content>
        <LayoutFooter />
      </Layout>
    </Layout>
  )
}
